import React, { useState, useEffect, useMemo, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import './reasonreg.css';
import { useForm } from 'react-hook-form';
import { convertTo24Hour } from '../Inputfield/inputfield';
import Swal from 'sweetalert2';
import { Autocomplete, Tooltip } from '@mui/material';
import { shiftadd } from '../../Services/app/masterservice';
import { CustomDaySelect } from '../Inputfield/inputfield';
import { cleanCustomerId, customerbasedshift, isPmiCustomer } from '../../Services/app/operatorservice';
import { nextPlantSequenceCode } from '../../Shared/utils/plantRecordCode';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterPlantsForUser } from '../../Shared/utils/machineGroupAccess';
import { timeToSeconds } from '../../Shared/utils/holidaycalculation';

const secondsToHms = (totalSeconds) => {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const secondsToDayjs = (totalSeconds) =>
  dayjs(secondsToHms(totalSeconds ?? 0), 'HH:mm:ss');

const dayjsToHms = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format('HH:mm:ss') : '00:00:00';

export default function ReasonEdit({
  open,
  handleClose,
  handleAdd,
  dialogOpenCount,
  datasource,
  setDatasource,
  customerId,
  dialogData,
  reasonKey = 'reason',
  groupKey = 'reasongroups',
  isQuality = false,
}) {
  const memoizedDatasource = useMemo(() => datasource, [datasource]);
  const memoizedDialogData = useMemo(() => dialogData, [dialogData]);
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const hasReasonEditAccess = userDetails?.reasonEditAccess === true;
  const customDaySelectRef = useRef();
  const modeSelectRef = useRef();
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';

  const [shiftCategory, setShiftCategory] = useState([]);
  const [shiftsmode, setShiftsmode] = useState([]);

  const resolvedGroupKey = useMemo(() => {
    return groupKey || 'reasongroups';
  }, [groupKey]);

  const resolvedReasonKey = useMemo(() => {
    if (memoizedDialogData?.group?.toLowerCase() === 'quality') return 'qualityreason';
    if (isQuality) return 'qualityreason';
    return reasonKey || 'reason';
  }, [isQuality, reasonKey, memoizedDialogData]);

  const { register, handleSubmit, formState: { errors }, trigger, setValue, reset, unregister, watch } = useForm({
    shouldFocusError: true,
    mode: 'onBlur',
    defaultValues: {
      reason: memoizedDialogData?.reason || '',
      code: memoizedDialogData?.code || '',
      mode: memoizedDialogData?.mode || '',
      category: memoizedDialogData?.category || '',
      group: memoizedDialogData?.group || '',
      plant_code: memoizedDialogData?.plant_code || '',
      thresholdTime: secondsToDayjs(memoizedDialogData?.thresholdTime)
    }
  });

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setShiftForm(prev => ({ ...prev, [name]: value }));
    setValue(name, value, { shouldValidate: true });
  };

  const handleTimeChange = (name, value) => {
    setShiftForm(prev => ({ ...prev, [name]: value }));
    setValue(name, value, { shouldValidate: true });
  };

  // Super Admin only: multi-select Plant checkboxes. Plants already carrying
  // this reason start checked; unchecking one deletes just that plant's
  // record on save, checking a new one creates a record for it.
  const handlePlantCodesChange = (event) => {
    const { value } = event.target;
    let nextValues = typeof value === 'string' ? value.split(',') : value;

    if (Array.isArray(nextValues) && nextValues.includes('all')) {
      const allPlantCodes = visiblePlantOptions.map((p) => p.code).filter(Boolean);
      const currentlyAllSelected =
        Array.isArray(shiftForm.plant_codes) && shiftForm.plant_codes.length === allPlantCodes.length;
      nextValues = currentlyAllSelected ? [] : allPlantCodes;
    }

    setShiftForm((prev) => ({ ...prev, plant_codes: nextValues }));
    setValue('plant_codes', nextValues, { shouldValidate: true });
  };

  const defaultShiftForm = useMemo(() => ({
    reason: memoizedDialogData?.reason || '',
    code: memoizedDialogData?.code || '',
    mode: memoizedDialogData?.mode || '',
    category: memoizedDialogData?.category || '',
    group: memoizedDialogData?.group || '',
    plant_code: memoizedDialogData?.plant_code || '',
    plant_codes: memoizedDialogData?.plant_code ? [memoizedDialogData.plant_code] : [],
    thresholdTime: secondsToDayjs(memoizedDialogData?.thresholdTime),
  }), [memoizedDialogData]);

  const [shiftForm, setShiftForm] = useState(defaultShiftForm);
  const [reasonGroupOptions, setReasonGroupOptions] = useState([]);
  const [plantOptions, setPlantOptions] = useState([]);
  const [reasonSettingEnabled, setReasonSettingEnabled] = useState(false);
  const showPlantField = isPmiCustomer(customerId) && plantOptions.length > 0;
  const { userRole, isSuperAdmin } = useUserRole();
  // Threshold Time is a Super Admin only feature, and only for customers
  // with the `reason_setting` attribute turned on.
  const showThresholdFeature = reasonSettingEnabled && isSuperAdmin;
  const normalizeReasonName = (val) => (val || '').trim().toLowerCase();
  const userPlantCodes = userDetails?.plantAccess || [];
  // Non-Super-Admins only ever see the plant(s) assigned to them.
  const visiblePlantOptions = useMemo(
    () => filterPlantsForUser(plantOptions, userRole, userPlantCodes),
    [plantOptions, userRole, userPlantCodes]
  );

  useEffect(() => {
    fetchReasonGroups();
  }, [resolvedGroupKey, customerId]);

  useEffect(() => {
    const fetchPlantGroups = async () => {
      try {
        if (!isPmiCustomer(customerId)) {
          setPlantOptions([]);
          return;
        }
        const data = await customerbasedshift(customerId, 'plantgroups');
        const allPlantGroups = data?.[0]?.value || [];
        setPlantOptions(allPlantGroups.map((p) => ({ code: p.code, name: p.name || p.code })));
      } catch (error) {
        console.error('Error fetching plant groups:', error);
        setPlantOptions([]);
      }
    };
    fetchPlantGroups();
  }, [customerId]);

  useEffect(() => {
    const fetchReasonSetting = async () => {
      try {
        const data = await customerbasedshift(customerId, 'reason_setting');
        setReasonSettingEnabled(data?.[0]?.value === true || data?.[0]?.value === 'true');
      } catch (error) {
        console.error('Error fetching reason setting:', error);
        setReasonSettingEnabled(false);
      }
    };
    fetchReasonSetting();
  }, [customerId]);

  const fetchReasonGroups = async () => {
    // const key = 'reasongroups';
    try {
      const data = await customerbasedshift(customerId, resolvedGroupKey);
      const allReasonGroups = data[0]?.value || [];
      const mappedGroups = allReasonGroups.map((item) => ({
        value: item.groupName,
        label: item.groupName,
      }));
      setReasonGroupOptions(mappedGroups);
    } catch (error) {
      console.error("Error fetching reason groups:", error);
      setReasonGroupOptions([]);
    }
  };

  const onSubmit = async (data) => {
    try {
      const shiftIdToEdit = memoizedDialogData?.id;
      const targetKey =
        shiftForm.group?.toLowerCase() === 'quality' ? 'qualityreason' : 'reason';

      const normalizedReason = shiftForm.reason
        ?.replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const plantCode = showPlantField ? (shiftForm.plant_code || undefined) : undefined;

      const updatedShiftData = {
        id: shiftIdToEdit,
        reason: shiftForm.reason?.trim(),
        code: shiftForm.code,
        mode: shiftForm.mode,
        category: hideCategory ? "" : shiftForm.category,
        group: shiftForm.group || "",
        thresholdTime: timeToSeconds(dayjsToHms(shiftForm.thresholdTime)),
        // No field for this in the edit form (toggled inline on the card
        // grid instead) - carry the existing value through so editing a
        // disabled reason doesn't silently re-enable it. Missing/undefined
        // defaults to disabled, matching the card toggle's default-off state.
        enabled: memoizedDialogData?.enabled === true,
        ...(showPlantField && {
          plant_code: shiftForm.plant_code || undefined,
        }),
      };

      const scope = 'SERVER_SCOPE';

      // helper to extract id safely
      const getId = (val) =>
        typeof val === 'object' && val !== null ? val.$oid : val;

      if (targetKey !== resolvedReasonKey) {
        // ===============================
        // 🔁 GROUP CHANGED FLOW
        // ===============================

        // 🚨 Check duplicate in target list FIRST
        const targetData = await customerbasedshift(customerId, targetKey);
        let targetList = targetData?.[0]?.value || [];

        const isDuplicate = targetList.some((item) => {
          return (
            item.reason?.trim().toLowerCase() === normalizedReason &&
            (item.plant_code || undefined) === plantCode &&
            getId(item.id) !== getId(shiftIdToEdit)
          );
        });

        if (isDuplicate) {
          handleClose();
          Swal.fire('Error', 'Duplicate Reason is not allowed.', 'error');
          return;
        }

        // ✅ Remove from source list
        const sourceData = await customerbasedshift(
          customerId,
          resolvedReasonKey
        );
        let sourceList = sourceData?.[0]?.value || [];

        sourceList = sourceList.filter(
          (item) => getId(item.id) !== getId(shiftIdToEdit)
        );

        await shiftadd(
          { [resolvedReasonKey]: sourceList, lastUpdateTs: Date.now() },
          customerId,
          scope
        );

        // ✅ Add to target list
        targetList.push(updatedShiftData);

        const response = await shiftadd(
          { [targetKey]: targetList, lastUpdateTs: Date.now() },
          customerId,
          scope
        );

        if (response.msg) {
          Swal.fire(response.msg);
        } else {
          Swal.fire('Updated Successfully');
        }
      } else if (showPlantField && isSuperAdmin) {
        // ===============================
        // 🏭 SUPER ADMIN MULTI-PLANT SYNC
        // Checkboxes drive plant-wise records for this reason: kept plants
        // are updated in place, newly checked plants get a new record, and
        // unchecked plants have their record for this reason deleted.
        // ===============================

        const sourceData = await customerbasedshift(customerId, resolvedReasonKey);
        let existingShifts = sourceData?.[0]?.value || [];

        const originalNormalizedReason = normalizeReasonName(memoizedDialogData?.reason);
        const siblingRecords = existingShifts.filter(
          (item) => normalizeReasonName(item.reason) === originalNormalizedReason
        );
        const siblingIds = siblingRecords.map((item) => getId(item.id));
        const selectedPlantCodes = [...new Set((shiftForm.plant_codes || []).filter(Boolean))];

        if (selectedPlantCodes.length === 0) {
          handleClose();
          Swal.fire('Error', 'Select at least one plant.', 'error');
          return;
        }

        // Block only when a selected plant already has a *different* reason
        // with this name (not one of our own siblings being kept/moved).
        const conflictingPlants = selectedPlantCodes.filter((pc) => {
          const match = existingShifts.find(
            (item) =>
              normalizeReasonName(item.reason) === normalizedReason &&
              (item.plant_code || undefined) === pc
          );
          return match && !siblingIds.includes(getId(match.id));
        });

        if (conflictingPlants.length > 0) {
          handleClose();
          const plantNames = conflictingPlants.map(
            (pc) => plantOptions.find((p) => p.code === pc)?.name || pc
          );
          Swal.fire(
            'Error',
            `Duplicate Reason is not allowed for plant(s): ${plantNames.join(', ')}`,
            'error'
          );
          return;
        }

        // Drop all of this reason's existing plant records, then rebuild
        // from the checked plants (kept ones preserve id/code).
        let nextList = existingShifts.filter((item) => !siblingIds.includes(getId(item.id)));

        let addedCount = 0;
        selectedPlantCodes.forEach((pc) => {
          const existingSibling = siblingRecords.find((s) => (s.plant_code || undefined) === pc);
          const isNew = !existingSibling;
          if (isNew) addedCount += 1;
          nextList.push({
            id: existingSibling ? existingSibling.id : Math.random().toString(36).substr(2, 9),
            reason: shiftForm.reason?.trim(),
            code: existingSibling ? existingSibling.code : String(nextPlantSequenceCode(nextList, pc)),
            mode: shiftForm.mode,
            category: hideCategory ? "" : shiftForm.category,
            group: shiftForm.group || "",
            thresholdTime: timeToSeconds(dayjsToHms(shiftForm.thresholdTime)),
            // Each plant's own enabled/disabled state is independent - keep
            // it for existing siblings (missing/undefined defaults to
            // disabled), default new ones to enabled.
            enabled: existingSibling ? existingSibling.enabled === true : true,
            plant_code: pc,
          });
        });
        const removedCount = siblingRecords.length - (selectedPlantCodes.length - addedCount);

        const formData = {
          [resolvedReasonKey]: nextList,
          lastUpdateTs: Date.now(),
        };

        const response = await shiftadd(formData, customerId, scope);

        if (response.msg) {
          Swal.fire(response.msg);
        } else {
          const parts = [];
          if (addedCount > 0) parts.push(`${addedCount} plant(s) added`);
          if (removedCount > 0) parts.push(`${removedCount} plant(s) removed`);
          Swal.fire(parts.length > 0 ? `Updated Successfully (${parts.join(', ')})` : 'Updated Successfully');
        }
      } else {
        // ===============================
        // ✏️ SAME LIST UPDATE FLOW
        // ===============================

        const sourceData = await customerbasedshift(
          customerId,
          resolvedReasonKey
        );
        let existingShifts = sourceData?.[0]?.value || [];

        // 🚨 Duplicate check in same list (exclude self)
        const isDuplicate = existingShifts.some((item) => {
          return (
            item.reason?.trim().toLowerCase() === normalizedReason &&
            (item.plant_code || undefined) === plantCode &&
            getId(item.id) !== getId(shiftIdToEdit)
          );
        });

        if (isDuplicate) {
          handleClose();
          Swal.fire('Error', 'Duplicate Reason is not allowed.', 'error');
          return;
        }

        let existingShiftIndex = -1;
        let existingIdStructure = null;

        if (shiftIdToEdit) {
          existingShiftIndex = existingShifts.findIndex(
            (item) => getId(item.id) === getId(shiftIdToEdit)
          );

          if (existingShiftIndex !== -1) {
            existingIdStructure = existingShifts[existingShiftIndex].id;
          }
        }

        if (existingShiftIndex === -1) {
          console.error('Shift not found for ID:', shiftIdToEdit);
          Swal.fire(
            'Error',
            'Shift not found for update. Please try again.',
            'error'
          );
          handleClose();
          reset(defaultShiftForm);
          return;
        }

        updatedShiftData.id = existingIdStructure;
        existingShifts[existingShiftIndex] = updatedShiftData;

        const formData = {
          [resolvedReasonKey]: existingShifts,
          lastUpdateTs: Date.now(),
        };

        const response = await shiftadd(formData, customerId, scope);

        if (response.msg) {
          Swal.fire(response.msg);
        } else {
          Swal.fire('Updated Successfully');
        }
      }

      handleClose();
      reset(defaultShiftForm);
    } catch (error) {
      handleClose();
      reset(defaultShiftForm);
      console.error('Error submitting shift data:', error);
      Swal.fire('Error submitting shift data: ' + error.message);
    }
  };

  useEffect(() => {
    const fallbackOptions = [
      { value: 'Planned downtime', label: 'Planned Downtime' },
      { value: 'Unplanned downtime', label: 'Unplanned Downtime' },
    ];
    setShiftCategory(fallbackOptions);

    const fallbackOptions1 = [
      { value: 'Men', label: 'Men' },
      { value: 'Machine', label: 'Machine' },
      { value: 'MotherNature', label: 'MotherNature' },
      { value: 'Material', label: 'Material' },
      { value: 'Method', label: 'Method' },
      { value: 'Measurement', label: 'Measurement' },
    ];
    setShiftsmode(fallbackOptions1);

    if (!open) {
      reset(defaultShiftForm);
      setShiftForm(defaultShiftForm);
    } else if (memoizedDialogData) {
      const siblingPlantCodes = showPlantField && isSuperAdmin
        ? (Array.isArray(datasource) ? datasource : [])
            .filter((item) => normalizeReasonName(item.reason) === normalizeReasonName(memoizedDialogData.reason))
            .map((item) => item.plant_code)
            .filter(Boolean)
        : [];

      const initialFormState = {
        reason: memoizedDialogData.reason || '',
        code: memoizedDialogData.code || '',
        mode: memoizedDialogData.mode || '',
        category: memoizedDialogData.category || '',
        group: memoizedDialogData.group || '',
        plant_code: memoizedDialogData.plant_code || '',
        plant_codes: siblingPlantCodes,
        thresholdTime: secondsToDayjs(memoizedDialogData?.thresholdTime),
      };

      setShiftForm(initialFormState);
      reset(initialFormState);

      Object.keys(initialFormState).forEach(key => {
        setValue(key, initialFormState[key]);
      });
    }
  }, [open, reset, defaultShiftForm, memoizedDialogData, setValue, datasource, showPlantField, isSuperAdmin]);

  // Non-Super-Admins with exactly one assigned plant don't need to pick —
  // select it for them automatically (e.g. a legacy record with no plant yet).
  useEffect(() => {
    if (!open || !showPlantField || isSuperAdmin) return;
    if (visiblePlantOptions.length === 1 && !shiftForm.plant_code) {
      const onlyCode = visiblePlantOptions[0].code;
      setShiftForm((prev) => ({ ...prev, plant_code: onlyCode }));
      setValue('plant_code', onlyCode);
      trigger('plant_code');
    }
  }, [open, showPlantField, isSuperAdmin, visiblePlantOptions, shiftForm.plant_code, setValue, trigger]);

  const formValues = watch();
  useEffect(() => {
    setShiftForm(formValues);
  }, [formValues]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (modeSelectRef.current) {
          const selectElement = modeSelectRef.current;
          if (selectElement) {
            selectElement.focus();
            selectElement.click();
          }
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open]);
  const hideCategory =
    [window._env_.CUSTOMER_ID, window._env_.GPLAST_CUSTOMER_ID].includes(cleanCustomerId(customerId)) &&
    (shiftForm.group || "").trim().toLowerCase() === "quality";

  useEffect(() => {
    if (hideCategory) {
      unregister("category");
      setShiftForm((prev) => ({ ...prev, category: "" }));
    }
  }, [hideCategory, unregister]);


  const isSaveDisabled =
    !shiftForm?.mode ||
    !shiftForm?.reason?.trim() ||
    (!hideCategory && !shiftForm?.category) ||
    (showPlantField &&
      (isSuperAdmin ? !(shiftForm?.plant_codes?.length > 0) : !shiftForm?.plant_code));

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="300px" PaperProps={{ style: { backgroundColor: dialogBackgroundColor } }}>
      <DialogTitle style={{ color: 'black' }}>Edit Reason</DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} style={{ backgroundColor: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <div className="filter_sec">
          <form onSubmit={handleSubmit(onSubmit)} className="form_sec shift_form" autoComplete="off">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="form_sec_fields">

                <div className={`form_field  ${errors.reason ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("reason", {
                      required: "Reason is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      }
                    })}
                    onBlur={() => trigger('reason')}
                    label="Reason"
                    type="text"
                    name="reason"
                    disabled={!hasReasonEditAccess}
                    value={shiftForm.reason}
                    onChange={handleFormChange}
                    error={!!errors.reason}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.reason && <div className="mat-error">{errors.reason.message}</div>}
                </div>
                {showThresholdFeature && (
                  <div className={`form_field  ${errors.thresholdTime ? 'error-outline' : ''}`}>
                    <DemoItem>
                      <TimePicker
                        {...register("thresholdTime", {
                          validate: (value) => {
                            if (!value) return true;
                            if (!dayjs(value).isValid()) return "Invalid time";
                            return true;
                          }
                        })}
                        onBlur={() => trigger('thresholdTime')}
                        value={shiftForm.thresholdTime}
                        onChange={(value) => handleTimeChange('thresholdTime', value)}
                        views={['hours', 'minutes', 'seconds']}
                        format="HH:mm:ss"
                        ampm={false}
                        label="Threshold Time"
                        error={!!errors.thresholdTime}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: errors.thresholdTime?.message,
                          },
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'black',
                            },
                            '&:hover fieldset': {
                              borderColor: 'black',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'orange',
                            },
                            '& .MuiOutlinedInput-input': {
                              color: 'black',
                            },
                            '&.Mui-focused .MuiOutlinedInput-input': {
                              caretColor: 'orange',
                            },
                          },
                        }}
                      />
                    </DemoItem>
                  </div>
                )}
                {/* <div className={`form_field  ${errors.code ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("code", {
                      required: "Code is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value => {
                        if (isNaN(value)) return "Code must be a number";
                        if (Number(value) <= 0) return "Code must be greater than 0";
                        return true;
                      }
                    })}
                    onBlur={() => trigger('code')}
                    label="Code"
                    type="number"
                    name="code"
                    disabled
                    value={shiftForm.code}
                    onChange={handleFormChange}
                    error={!!errors.code}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.code && <div className="mat-error">{errors.code.message}</div>}
                </div> */}
                {[window._env_.GPLAST_CUSTOMER_ID, window._env_.CUSTOMER_ID].includes(cleanCustomerId(customerId)) && (
                  <div className="form_field">
                    <Autocomplete
                      options={reasonGroupOptions}
                      getOptionLabel={(option) => option.label}
                      value={
                        reasonGroupOptions.find((opt) => opt.value === shiftForm.group) || null
                      }
                      onChange={(event, newValue) => {
                        const value = newValue ? newValue.value : '';
                        setShiftForm({ ...shiftForm, group: value });
                        setValue('group', value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Reason Group"
                          variant="outlined"
                          fullWidth
                          InputLabelProps={{
                            sx: {
                              color: 'black',
                              '&.Mui-focused': { color: 'orange' },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: 'black' },
                              '&:hover fieldset': { borderColor: 'black' },
                              '&.Mui-focused fieldset': { borderColor: 'orange' },
                              '& .MuiOutlinedInput-input': { color: 'black' },
                              '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                )}
                {showPlantField && (isSuperAdmin ? (
                  <div className={`form_field  ${errors.plant_codes ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_codes", {
                        validate: (value) => (value && value.length > 0) || "Select at least one plant",
                      })}
                      name="plant_codes"
                      value={shiftForm.plant_codes}
                      onChange={handlePlantCodesChange}
                      label="Select Plant(s)"
                      required={true}
                      multiple
                      showSelectAll={visiblePlantOptions.length > 1}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_codes}
                    />
                    {errors.plant_codes && <div className="mat-error">Select at least one plant</div>}
                  </div>
                ) : (
                  <div className={`form_field  ${errors.plant_code ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_code", { required: "Plant is required" })}
                      onBlur={() => trigger('plant_code')}
                      name="plant_code"
                      value={shiftForm.plant_code}
                      onChange={handleFormChange}
                      label="Select Plant"
                      required={true}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_code}
                    />
                    {errors.plant_code && <div className="mat-error">Plant is required</div>}
                  </div>
                ))}
                <div className={`form_field  ${errors.mode ? 'error-outline' : ''}`}>
                  <CustomDaySelect
                    {...register("mode", { required: "Mode is required" })}
                    name="mode"
                    label="Select Mode"
                    required={true}
                    value={shiftForm.mode}
                    onChange={handleFormChange}
                    onBlur={() => trigger("mode")}
                    options={shiftsmode}
                    error={!!errors.mode}
                    ref={modeSelectRef}

                  />

                  {errors.mode && <div className="mat-error">Mode is required</div>}
                </div>
                {!hideCategory && (
                  <div className={`form_field ${errors.category ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("category", { required: "Category is required" })}
                      onBlur={() => trigger('category')}
                      ref={customDaySelectRef}
                      name="category"
                      value={shiftForm.category}
                      onChange={handleFormChange}
                      label="Select Category"
                      required={true}
                      options={shiftCategory}
                      error={!!errors.category}
                    />
                    {errors.category && <div className="mat-error">Category is required</div>}
                  </div>
                )}

              </div>
            </LocalizationProvider>
            <div className="form-button text-right" align="end" style={{ marginRight: '10px' }}>
              <Button type="submit" variant="contained" className="filter_btn btn_orange" color="warning" disabled={isSaveDisabled}>
                Save
              </Button>
            </div>
            <br></br>
          </form>
        </div>
      </div>
    </Dialog>
  );
}